import { useState, type ReactNode } from "react";
import { ChevronDownIcon, InfoIcon } from "lucide-react";

import { relative } from "@/components/tools/assistant/format";
import {
  ASK_STATUS,
  type ChipDef,
  DEFERRAL_REASON,
  ITEM_STATUS,
  MESSAGE_KIND,
  ORIGIN,
  RESOLUTION_KIND,
  SCHEDULE_ACTION,
  SCHEDULE_SOURCE,
  type Tone,
  URGENCY,
} from "@/components/tools/assistant/vocab";

import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";

const TONES: Record<Tone, string> = {
  slate: "border-slate-200 bg-slate-100 text-slate-700",
  green: "border-green-200 bg-green-50 text-green-700",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  orange: "border-orange-200 bg-orange-50 text-orange-700",
  red: "border-red-200 bg-red-50 text-red-700",
  blue: "border-blue-200 bg-blue-50 text-blue-700",
  sky: "border-sky-200 bg-sky-50 text-sky-700",
  indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
  teal: "border-teal-200 bg-teal-50 text-teal-700",
  pink: "border-pink-200 bg-pink-50 text-pink-700",
};

/** A coloured, iconed pill. `detail` is appended after a dot. */
export const Chip = ({
  def,
  detail,
  count,
  className,
}: {
  def: ChipDef;
  detail?: ReactNode;
  count?: number;
  className?: string;
}) => {
  const { Icon } = def;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium",
        TONES[def.tone],
        count === 0 && "opacity-40",
        className,
      )}
      title={typeof detail === "string" ? `${def.hint}\n\n${detail}` : def.hint}
    >
      <Icon className="size-3.5 shrink-0" />
      {count !== undefined && <span className="font-semibold">{count}</span>}
      {def.label}
      {detail ? (
        <span className="max-w-[360px] truncate font-normal opacity-80">
          · {detail}
        </span>
      ) : null}
    </span>
  );
};

/** A plain pill for free text, e.g. a tool name. */
export const Tag = ({
  children,
  tone = "slate",
}: {
  children: ReactNode;
  tone?: Tone;
}) => (
  <span
    className={cn(
      "inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs",
      TONES[tone],
    )}
  >
    {children}
  </span>
);

/** A labelled "why" line: an icon chip followed by the reason in plain text. */
export const Why = ({ def, children }: { def: ChipDef; children: ReactNode }) => (
  <div className="flex flex-wrap items-center gap-1.5 text-xs">
    <Chip def={def} />
    <span className="text-slate-700">{children}</span>
  </div>
);

export const WhenCell = ({ iso }: { iso: string | null | undefined }) =>
  iso ? (
    <div className="flex flex-col">
      <span className="text-sm text-slate-700">
        {dates.formatShortDateTime(iso)}
      </span>
      <span className="text-xs text-slate-500">{relative(iso)}</span>
    </div>
  ) : (
    <span className="text-slate-400">—</span>
  );

export const StatCard = ({
  label,
  value,
  hint,
  tone = "slate",
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}) => (
  <div
    className={cn(
      "flex min-w-[170px] flex-1 flex-col gap-1 rounded-xl border p-4",
      TONES[tone],
    )}
  >
    <span className="text-xs font-medium uppercase tracking-wide opacity-80">
      {label}
    </span>
    <span className="text-2xl font-semibold">{value}</span>
    {hint ? <span className="text-xs opacity-80">{hint}</span> : null}
  </div>
);

const LEGEND: { title: string; defs: ChipDef[] }[] = [
  { title: "Messages", defs: Object.values(MESSAGE_KIND) },
  { title: "Why it was sent then", defs: Object.values(ORIGIN) },
  { title: "Delayed because", defs: Object.values(DEFERRAL_REASON) },
  { title: "Email status", defs: Object.values(ITEM_STATUS) },
  { title: "Each ask", defs: Object.values(ASK_STATUS) },
  { title: "How it was handled", defs: Object.values(RESOLUTION_KIND) },
  { title: "Urgency", defs: Object.values(URGENCY) },
  { title: "Schedule type", defs: Object.values(SCHEDULE_ACTION) },
  { title: "Set by", defs: Object.values(SCHEDULE_SOURCE) },
];

/** Collapsible key to every chip on the page. */
export const Legend = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border bg-white">
      <button
        className="flex w-full items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700"
        onClick={() => {
          setOpen(!open);
        }}
        type="button"
      >
        <InfoIcon className="size-4" />
        What the chips mean
        <ChevronDownIcon
          className={cn("ml-auto size-4 transition-transform", open && "rotate-180")}
        />
      </button>
      {open && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4 border-t px-4 py-3">
          {LEGEND.map((group) => (
            <div className="flex flex-col gap-1.5" key={group.title}>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {group.title}
              </span>
              {group.defs.map((d) => (
                <div className="flex items-start gap-2 text-xs" key={d.label}>
                  <Chip className="shrink-0" def={d} />
                  <span className="pt-0.5 text-slate-600">{d.hint}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
